import { useRef, useState } from "react";
import { Upload, X, Loader2, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const FILE_ERROR = "Files must be PDF, JPG, PNG or HEIC and under 10 MB";
const OK_EXT = ["pdf", "jpg", "jpeg", "png", "heic"];

export const DocPicker = ({ kind, label, hint, max, files, onChange, error, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setErr("");
    if (files.length + list.length > max) { setErr(`Upload at most ${max} file${max > 1 ? "s" : ""}`); return; }
    const bad = list.find((f) => !OK_EXT.includes(f.name.split(".").pop().toLowerCase()) || f.size > 10 * 1024 * 1024);
    if (bad) { setErr(FILE_ERROR); return; }
    setBusy(true);
    const added = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      try {
        const { data } = await api.post("/applications/files", fd);
        added.push(data);
      } catch (e2) {
        setErr(apiError(e2).message);
        break;
      }
    }
    onChange([...files, ...added]);
    setBusy(false);
  };

  const shown = err || error;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[13px] font-semibold">{label}</div>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy || files.length >= max} data-testid={`${testid}-button`}
        className={cn("flex w-full items-center justify-center gap-2 rounded-md border border-dashed bg-card px-4 py-5 text-sm transition-colors hover:border-primary disabled:opacity-60", shown && "border-destructive")}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {busy ? "Uploading…" : files.length >= max ? "Maximum reached" : "Choose file" + (max > 1 ? "s" : "")}
      </button>
      <input ref={ref} type="file" className="hidden" multiple={max > 1} accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic" onChange={pick} data-testid={`${testid}-input`} />
      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2 rounded border bg-muted/40 px-3 py-2 text-xs" data-testid={`${testid}-file`}>
              <span className="flex min-w-0 items-center gap-2"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.filename}</span></span>
              <button type="button" onClick={() => onChange(files.filter((x) => x.id !== f.id))} aria-label="Remove file"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      {shown ? <p className="text-xs font-medium text-destructive" data-testid={`${testid}-error`}>{shown}</p> : hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};
